import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Filter, ChevronDown } from "lucide-react";
import { cn } from "../../utils/cn";

interface ActionMenuProps {
    onAdd?: () => void;
    onFilter: () => void;
    activeFilterCount: number;
}

export function ActionMenu({ onAdd, onFilter, activeFilterCount }: ActionMenuProps) {
    const [isOpen, setIsOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Close on click outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={menuRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`
                    flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all duration-200
                    ${isOpen
                        ? 'bg-siemens-deep-blue text-white border-siemens-deep-blue'
                        : 'bg-white dark:bg-siemens-elevation-base-1 border-slate-200 dark:border-siemens-divider text-siemens-deep-blue dark:text-siemens-text-standard hover:border-siemens-petrol'
                    }
                `}
            >
                <div className="flex items-center gap-1.5">
                    <span className="text-sm font-semibold">Actions</span>
                </div>
                {activeFilterCount > 0 && (
                    <span className="bg-white text-siemens-petrol text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[1.25rem] h-5 flex items-center justify-center">
                        {activeFilterCount}
                    </span>
                )}
                <ChevronDown className={cn("w-4 h-4 transition-transform duration-200", isOpen && "rotate-180")} />
            </button>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-[#1a1a24] rounded-xl shadow-xl border border-siemens-deep-blue/10 dark:border-[#3d3d4d] overflow-hidden z-[60]"
                    >
                        <div className="p-1.5 space-y-1">
                            {onAdd && (
                                <button
                                    onClick={() => { onAdd(); setIsOpen(false); }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-siemens-deep-blue dark:text-white hover:bg-siemens-deep-blue-8/50 dark:hover:bg-white/10 rounded-lg transition-colors group"
                                >
                                    <div className="w-8 h-8 rounded-full bg-siemens-petrol/10 flex items-center justify-center group-hover:bg-siemens-petrol group-hover:text-white transition-colors text-siemens-petrol">
                                        <Plus className="w-4 h-4" />
                                    </div>
                                    Create New Epic
                                </button>
                            )}

                            <button
                                onClick={() => { onFilter(); setIsOpen(false); }}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-semibold text-siemens-deep-blue dark:text-white hover:bg-siemens-deep-blue-8/50 dark:hover:bg-white/10 rounded-lg transition-colors group"
                            >
                                <div className="w-8 h-8 rounded-full bg-siemens-deep-blue-8 flex items-center justify-center group-hover:bg-siemens-petrol group-hover:text-white transition-colors text-siemens-text-soft">
                                    <Filter className="w-4 h-4" />
                                </div>
                                <div className="flex-1 text-left flex items-center justify-between">
                                    <span>Filter Epics</span>
                                    {activeFilterCount > 0 && (
                                        <span className="bg-siemens-petrol text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                                            {activeFilterCount}
                                        </span>
                                    )}
                                </div>
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default ActionMenu;
